import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled, { css } from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  minBreakpointQuery,
  sectionPaddings,
} from '../styles';
import { Button, Container, HtmlContent } from './ui';
import PardotForm from './PardotForm';

const StyledSubBanner = styled.section`
  ${sectionPaddings('30px', '80px')};
  background-color: ${brandColours.primary};
`;

const StyledInner = styled.div`
  display: grid;
  gap: 40px;

  ${minBreakpointQuery.small`
    gap: 60px;
  `}

  ${minBreakpointQuery.medium`
    grid-template-columns: repeat(2, 1fr);
    align-items: center;
  `}

  ${minBreakpointQuery.mlarge`
    gap: 80px;
  `}

  ${minBreakpointQuery.large`
    gap: 100px;
  `}

  ${minBreakpointQuery.xlarge`
    gap: 120px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 150px;
  `}

  ${minBreakpointQuery.xxxlarge`
    gap: 180px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    gap: 220px;
  `}
`;

const StyledContent = styled.div``;

const StyledHeading = styled.h1`
  color: ${brandColours.secondary};
  ${fluidFontSize(
    '30px',
    '50px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
  line-height: 1.2;
`;

const StyledText = styled(HtmlContent)`
  margin-top: 18px;

  ${minBreakpointQuery.small`
    margin-top: 22px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 26px;
  `}
`;

const StyledButton = styled(Button)`
  margin-top: 18px;

  ${minBreakpointQuery.small`
    margin-top: 22px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 26px;
  `}
`;

const StyledGatsbyImage = styled(GatsbyImage)`
  flex-shrink: 0;
  margin: auto;
  width: 160px;
  height: 160px;
  border-radius: 50%;

  ${minBreakpointQuery.tsmall`
    width: 200px;
    height: 200px;
  `}

  ${minBreakpointQuery.smedium`
    width: 240px;
    height: 240px;
  `}

  ${minBreakpointQuery.mlarge`
    width: 280px;
    height: 280px;
  `}

  ${minBreakpointQuery.xlarge`
    width: 320px;
    height: 320px;
  `}
`;

const StyledImage = styled.img`
  width: 100%;
  max-height: 280px;

  ${minBreakpointQuery.tiny`
    max-height: 300px;
  `}

  ${minBreakpointQuery.small`
    max-height: 320px;
  `}

  ${minBreakpointQuery.large`
    max-height: 340px;
  `}

  ${minBreakpointQuery.xxlarge`
    max-height: 360px;
  `}

  ${({ $inContent }) => {
    if ($inContent) {
      return css`
        margin-top: 30px;

        ${minBreakpointQuery.large`
          margin-top: 40px;
        `}
      `;
    }
  }}
`;

const StyledPardotForm = styled(PardotForm)`
  padding: 10px 0;
  background-color: ${brandColours.septenary};
  border-radius: 30px;
`;

const SubBanner = ({
  heading,
  text,
  link,
  image,
  pardotFormUrl,
  pardotFormHeight,
  isAuthor,
}) => (
  <StyledSubBanner>
    <Container>
      <StyledInner>
        <StyledContent>
          <StyledHeading>{heading}</StyledHeading>
          <StyledText {...(isAuthor ? { html: text } : { content: text })} />
          {link && <StyledButton to={link.pageUrl}>{link.text}</StyledButton>}
          {image && pardotFormUrl && (
            <StyledImage
              src={image.url}
              alt={image.alt}
              loading="eager"
              $inContent={true}
            />
          )}
        </StyledContent>
        {image &&
          !pardotFormUrl &&
          (isAuthor ? (
            <StyledGatsbyImage
              image={image.gatsbyImageData}
              alt={image.alt}
              loading="eager"
            />
          ) : (
            <StyledImage src={image.url} alt={image.alt} loading="eager" />
          ))}
        {pardotFormUrl && (
          <StyledPardotForm url={pardotFormUrl} height={pardotFormHeight} />
        )}
      </StyledInner>
    </Container>
  </StyledSubBanner>
);

export default SubBanner;
